import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLang } from '../hooks/useLang';
import Navbar from '../components/common/Navbar';
import '../styles/pages/Contact.scss';

const INTERESTS = [
    'General Inquiry',
    'Sharing Notes',
    'Partnerships',
    'Technical Support',
    'Feedback',
    'Other',
];

const COUNTRY_CODES = [
    { code: '+1', flag: '🇺🇸', label: 'US' },
    { code: '+44', flag: '🇬🇧', label: 'GB' },
    { code: '+994', flag: '🇦🇿', label: 'AZ' },
    { code: '+7', flag: '🇷🇺', label: 'RU' },
    { code: '+49', flag: '🇩🇪', label: 'DE' },
    { code: '+33', flag: '🇫🇷', label: 'FR' },
    { code: '+39', flag: '🇮🇹', label: 'IT' },
    { code: '+34', flag: '🇪🇸', label: 'ES' },
    { code: '+90', flag: '🇹🇷', label: 'TR' },
    { code: '+971', flag: '🇦🇪', label: 'AE' },
    { code: '+91', flag: '🇮🇳', label: 'IN' },
    { code: '+86', flag: '🇨🇳', label: 'CN' },
];

const SOCIAL_LINKS = [
    { href: '#', icon: 'fa-brands fa-facebook-f', label: 'Facebook' },
    { href: '#', icon: 'fa-brands fa-x-twitter', label: 'X (Twitter)' },
    { href: '#', icon: 'fa-brands fa-linkedin-in', label: 'LinkedIn' },
    { href: '#', icon: 'fa-brands fa-youtube', label: 'YouTube' },
    { href: '#', icon: 'fa-brands fa-instagram', label: 'Instagram' },
];

// ──────────────────────────────────────────────────────────────
// 📍 PUT YOUR COORDINATES HERE
const MAP_LAT = 40.376657346940675;   // latitude  (e.g. 40.4093 for Baku)
const MAP_LON = 49.85167836418479;   // longitude (e.g. 49.8671 for Baku)
const MAP_ZOOM = 5;        // zoom level (1–19, higher = closer)
// ──────────────────────────────────────────────────────────────

const Contact = () => {
    const { language } = useLang();

    const [form, setForm] = useState({
        name: '',
        email: '',
        countryCode: '+994',
        phone: '',
        interestedIn: '',
        message: '',
    });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState({ type: '', text: '' });
    const [loading, setLoading] = useState(false);

    // Validate a single field and return an error string (or '')
    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                return value.trim() ? '' : 'Name is required.';
            case 'email': {
                if (!value.trim()) return 'Email is required.';
                const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
                return emailOk ? '' : 'Please enter a valid email address.';
            }
            case 'phone':
                if (!value.trim()) return 'Phone number is required.';
                if (value.replace(/\s/g, '').length < 5) return 'Phone number is too short.';
                return '';
            case 'interestedIn':
                return value ? '' : 'Please select a topic.';
            case 'message':
                return value.trim() ? '' : 'Message is required.';
            default:
                return '';
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Phone: strip anything that is not a digit or space
        const sanitised = name === 'phone'
            ? value.replace(/[^\d\s]/g, '')
            : value;

        setForm(prev => ({ ...prev, [name]: sanitised }));

        // Clear error for this field as user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, sanitised) }));
        }

        setStatus({ type: '', text: '' });
    };

    // Auto-dismiss status messages after 15 seconds
    useEffect(() => {
        if (status.text) {
            const timer = setTimeout(() => {
                setStatus({ type: '', text: '' });
            }, 15000);
            return () => clearTimeout(timer);
        }
    }, [status]);

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields at once
        const fieldsToValidate = ['name', 'email', 'phone', 'interestedIn', 'message'];
        const newErrors = {};
        fieldsToValidate.forEach(field => {
            newErrors[field] = validateField(field, form[field]);
        });
        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some(Boolean);
        if (hasErrors) {
            setStatus({ type: 'error', text: 'Please fix the errors above before sending.' });
            return;
        }

        setLoading(true);
        setStatus({ type: '', text: '' });

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: form.name.trim(),
                    email: form.email.trim(),
                    phone: form.countryCode + ' ' + form.phone.trim(),
                    interestedIn: form.interestedIn,
                    message: form.message.trim(),
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                setStatus({ type: 'error', text: data.error || 'Something went wrong.' });
            } else {
                setStatus({ type: 'success', text: data.message });
                setForm({ name: '', email: '', countryCode: '+994', phone: '', interestedIn: '', message: '' });
                setErrors({});
            }
        } catch (err) {
            setStatus({ type: 'error', text: 'Network error. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterStatus, setNewsletterStatus] = useState({ type: '', text: '' });
    const [newsletterLoading, setNewsletterLoading] = useState(false);

    const handleNewsletterSubmit = async (e) => {
        e.preventDefault();
        setNewsletterLoading(true);
        setNewsletterStatus({ type: '', text: '' });

        try {
            const res = await fetch('/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: newsletterEmail }),
            });

            const data = await res.json();

            if (!res.ok) {
                setNewsletterStatus({ type: 'error', text: data.error || 'Something went wrong.' });
            } else {
                setNewsletterStatus({ type: 'success', text: data.message });
                setNewsletterEmail('');
            }
        } catch (err) {
            setNewsletterStatus({ type: 'error', text: 'Network error.' });
        } finally {
            setNewsletterLoading(false);
        }
    };

    // Auto-dismiss newsletter status messages after 15 seconds
    useEffect(() => {
        if (newsletterStatus.text) {
            const timer = setTimeout(() => {
                setNewsletterStatus({ type: '', text: '' });
            }, 15000);
            return () => clearTimeout(timer);
        }
    }, [newsletterStatus]);

    return (
        <div className="contact-wrapper">
            <Navbar />

            {/* ── Hero / Form Section ── */}
            <section className="contact-hero">
                <motion.div
                    className="contact-hero-text"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <p className="contact-breadcrumb">YeniDers &bull; Contact us</p>
                    <h1>Contact us</h1>
                    <p className="contact-subtitle">
                        Get in touch and ask us anything. Whether you need help sharing notes,
                        have partnership ideas, or just a question — we answer it all.
                    </p>
                </motion.div>

                <motion.form
                    className="contact-form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
                >
                    {/* Row 1 */}
                    <div className="form-row">
                        <div className="form-field">
                            <input
                                type="text"
                                name="name"
                                placeholder="Your name *"
                                value={form.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.name ? 'input-error' : ''}
                            />
                            {errors.name && <span className="field-error">{errors.name}</span>}
                        </div>
                        <div className="form-field">
                            <input
                                type="email"
                                name="email"
                                placeholder="Email address *"
                                value={form.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={errors.email ? 'input-error' : ''}
                            />
                            {errors.email && <span className="field-error">{errors.email}</span>}
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="form-row">
                        <div className="form-field phone-field">
                            <select
                                name="countryCode"
                                value={form.countryCode}
                                onChange={handleChange}
                                className="country-select"
                                aria-label="Country code"
                            >
                                {COUNTRY_CODES.map(({ code, flag, label }) => (
                                    <option key={code} value={code}>
                                        {flag} {code}
                                    </option>
                                ))}
                            </select>
                            <div className="phone-input-wrap">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    name="phone"
                                    placeholder="Phone number *"
                                    value={form.phone}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className={`phone-input${errors.phone ? ' input-error' : ''}`}
                                    maxLength={15}
                                />
                                {errors.phone && <span className="field-error">{errors.phone}</span>}
                            </div>
                        </div>
                        <div className="form-field">
                            <select
                                name="interestedIn"
                                value={form.interestedIn}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                aria-label="Interested in"
                                className={errors.interestedIn ? 'input-error' : ''}
                            >
                                <option value="" disabled>Interested in… *</option>
                                {INTERESTS.map((i) => (
                                    <option key={i} value={i}>{i}</option>
                                ))}
                            </select>
                            {errors.interestedIn && <span className="field-error">{errors.interestedIn}</span>}
                        </div>
                    </div>

                    {/* Message */}
                    <div className="form-field full-width">
                        <textarea
                            name="message"
                            placeholder="How can we help? *"
                            value={form.message}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            rows={5}
                            className={errors.message ? 'input-error' : ''}
                        />
                        {errors.message && <span className="field-error">{errors.message}</span>}
                    </div>

                    {/* Feedback */}
                    {status.text && (
                        <motion.div
                            className={`form-feedback ${status.type}`}
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {status.type === 'success'
                                ? <i className="fa-solid fa-circle-check" />
                                : <i className="fa-solid fa-circle-exclamation" />
                            }
                            {status.text}
                        </motion.div>
                    )}

                    <button type="submit" className="submit-btn" disabled={loading}>
                        {loading ? 'Sending…' : 'Send your message'}
                    </button>

                    <p className="form-disclaimer">
                        By clicking, you agree to our{' '}
                        <Link to={`/${language}/terms`}>Terms &amp; Conditions</Link> and{' '}
                        <Link to={`/${language}/privacy`}>Privacy Policy</Link>.
                    </p>
                </motion.form>
            </section>

            {/* ── Get in Touch Section ── */}
            <section className="contact-info-section">
                <motion.div
                    className="contact-info-inner"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <p className="info-location-label">YeniDers HQ</p>
                    <h2>Get in touch with YeniDers</h2>

                    <div className="info-map-row">
                        {/* ── Map ── */}
                        <div className="map-container">
                            <iframe
                                title="YeniDers location map"
                                src={`https://www.openstreetmap.org/export/embed.html?bbox=${MAP_LON - 0.01}%2C${MAP_LAT - 0.007}%2C${MAP_LON + 0.01}%2C${MAP_LAT + 0.007}&layer=mapnik&marker=${MAP_LAT}%2C${MAP_LON}&zoom=${MAP_ZOOM}`}
                                loading="lazy"
                                allowFullScreen
                            />
                            <a
                                href={`https://www.openstreetmap.org/?mlat=${MAP_LAT}&mlon=${MAP_LON}#map=${MAP_ZOOM}/${MAP_LAT}/${MAP_LON}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="map-link"
                            >
                                View larger map ↗
                            </a>
                        </div>

                        {/* ── Info card ── */}
                        <div className="info-card">
                            <div className="info-item">
                                <span className="info-label">
                                    <i className="fa-solid fa-location-dot" /> Address
                                </span>
                                <span className="info-value">Baku, Azerbaijan</span>
                            </div>
                            <div className="info-divider" />
                            <div className="info-item">
                                <span className="info-label">
                                    <i className="fa-solid fa-envelope" /> Email
                                </span>
                                <a href="mailto:support@yeniders.com" className="info-value link">
                                    support@yeniders.com
                                </a>
                            </div>
                            <div className="info-divider" />
                            <div className="info-item">
                                <span className="info-label">
                                    <i className="fa-solid fa-phone" /> Phone
                                </span>
                                <a href="tel:+994501234567" className="info-value link">
                                    +994 50 123 45 67
                                </a>
                            </div>
                            <div className="info-divider" />
                            <div className="info-socials">
                                {SOCIAL_LINKS.map(({ href, icon, label }) => (
                                    <a
                                        key={label}
                                        href={href}
                                        aria-label={label}
                                        className="social-icon"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <i className={icon} />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* ── Newsletter CTA ── */}
            <section className="contact-newsletter">
                <motion.div
                    className="newsletter-inner"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <div className="newsletter-text">
                        <h2>Get expert tips and study insights</h2>
                        <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                            <input
                                type="email"
                                placeholder="Email address"
                                value={newsletterEmail}
                                onChange={(e) => setNewsletterEmail(e.target.value)}
                                required
                            />
                            <button type="submit" disabled={newsletterLoading}>
                                {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
                            </button>
                        </form>
                        {newsletterStatus.text && (
                            <p className={`newsletter-status ${newsletterStatus.type}`}>
                                {newsletterStatus.text}
                            </p>
                        )}
                        <p className="newsletter-disclaimer">
                            By clicking, you agree to our{' '}
                            <Link to={`/${language}/terms`}>Terms &amp; Conditions</Link> &amp;{' '}
                            <Link to={`/${language}/privacy`}>Privacy Policy</Link>.
                        </p>
                    </div>
                </motion.div>
            </section>
        </div>
    );
};


export default Contact;
